import { ExternalLink } from 'lucide-react'

import { HubspotOrganizationData } from '@/api/organization-hubspot-client'
import { Icons } from '@/components/ui/icons'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useQueryGetOrganizationHubspotData } from '@/services/hubspot.service'

// Get HubSpot portal ID from environment config
const getHubspotPortalId = () =>
  globalThis.EnvConfig?.env?.HUBSPOT_PORTAL_ID || '25127966'

const getHubspotCompanyUrl = (companyId: string) =>
  `https://app.hubspot.com/contacts/${getHubspotPortalId()}/company/${companyId}`

type FieldConfig = {
  key: string
  label: string
  propertyKey?: string
}

const BASIC_FIELDS: FieldConfig[] = [
  { key: 'type', label: 'Type', propertyKey: 'type' },
  { key: 'sparte', label: 'Sparte', propertyKey: 'sparte' },
  { key: 'description', label: 'Description', propertyKey: 'description' },
  { key: 'marktrolle', label: 'Marktrolle', propertyKey: 'marktrolle' },
  {
    key: 'ziel_account_executive',
    label: 'Account Executive',
    propertyKey: 'ziel_account_executive'
  },
  { key: 'closedate', label: 'Close Date', propertyKey: 'closedate' },
  { key: 'pricing_tier', label: 'Pricing Tier' },
  {
    key: 'revenue_stream',
    label: 'Revenue Stream',
    propertyKey: 'revenue_stream'
  },
  { key: 'website', label: 'Website', propertyKey: 'website' }
]

const SIZE_MRR_FIELDS: FieldConfig[] = [
  { key: 'company_size', label: 'Company Size' },
  { key: 'current_mrr', label: 'Current MRR' },
  { key: 'potential_mrr', label: 'Potential MRR' },
  {
    key: 'potential_mrr__next_year_',
    label: 'Potential MRR (Next Year)',
    propertyKey: 'potential_mrr__next_year_'
  },
  {
    key: 'total_revenue',
    label: 'Total Revenue',
    propertyKey: 'total_revenue'
  },
  {
    key: 'annualrevenue',
    label: 'Annual Revenue',
    propertyKey: 'annualrevenue'
  },
  {
    key: 'anzahl_kunden__c',
    label: 'Anzahl Kunden',
    propertyKey: 'anzahl_kunden__c'
  },
  {
    key: 'customer_data_points__cdp_',
    label: 'Customer Data Points (CDP)',
    propertyKey: 'customer_data_points__cdp_'
  },
  { key: 'numberofemployees', label: 'Number of Employees' },
  {
    key: 'no__of_seats',
    label: 'Number of Seats',
    propertyKey: 'no__of_seats'
  },
  { key: 'no_of_users', label: 'Number of Users' }
]

const ADOPTION_FIELDS: FieldConfig[] = [
  { key: 'usecases_implemented', label: 'Use Cases Implemented' },
  { key: 'active_features', label: 'Active Features' },
  {
    key: 'aktive_produkte__c',
    label: 'Aktive Produkte',
    propertyKey: 'aktive_produkte__c'
  },
  {
    key: 'number_of_implemented_use_cases',
    label: 'Number of Implemented Use Cases',
    propertyKey: 'number_of_implemented_use_cases'
  }
]

const IT_LANDSCAPE_FIELDS: FieldConfig[] = [
  {
    key: 'customer_portal',
    label: 'Customer Portal',
    propertyKey: 'customer_portal'
  },
  {
    key: 'crm_system__c',
    label: 'CRM System',
    propertyKey: 'crm_system__c'
  },
  {
    key: 'archive_system',
    label: 'Archive System',
    propertyKey: 'archive_system'
  },
  {
    key: 'additional_software_in_use',
    label: 'Additional Software in Use',
    propertyKey: 'additional_software_in_use'
  }
]

const KNOWN_KEYS = new Set([
  ...BASIC_FIELDS.map((f) => f.propertyKey || f.key),
  ...SIZE_MRR_FIELDS.map((f) => f.propertyKey || f.key),
  ...ADOPTION_FIELDS.map((f) => f.propertyKey || f.key),
  ...IT_LANDSCAPE_FIELDS.map((f) => f.propertyKey || f.key),
  // Exclude keys that are already shown in the header or are internal HubSpot metadata
  'company_id',
  'company_name',
  'domain'
])

const getFieldValue = (
  data: HubspotOrganizationData,
  field: FieldConfig
): string => {
  // If there's a direct key on the data object, use it
  const directKey = field.key as keyof HubspotOrganizationData
  if (directKey in data && data[directKey] !== null && data[directKey] !== undefined) {
    const val = data[directKey]
    if (typeof val === 'string') return val
    if (typeof val === 'object') return JSON.stringify(val)
    return String(val)
  }

  // Otherwise, look in properties using the propertyKey
  const propKey = field.propertyKey || field.key
  const val = data.properties?.[propKey]
  if (val === null || val === undefined) return ''
  if (typeof val === 'string') return val
  return JSON.stringify(val)
}

const formatPropertyLabel = (key: string): string => {
  return key
    .replace(/__c$/g, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bHs\b/g, 'HS')
    .replace(/\bMrr\b/g, 'MRR')
    .replace(/\bCdp\b/g, 'CDP')
    .replace(/\bCrm\b/g, 'CRM')
}

const FieldGroup = ({
  title,
  fields,
  data
}: {
  title: string
  fields: FieldConfig[]
  data: HubspotOrganizationData
}) => (
  <div className="space-y-3">
    <h3 className="text-sm font-semibold text-primary border-b pb-1">
      {title}
    </h3>
    <div className="grid grid-cols-2 gap-3">
      {fields.map((field) => {
        const value = getFieldValue(data, field)
        return (
          <div className="space-y-1" key={field.key}>
            <Label className="text-xs text-muted-foreground">
              {field.label}
            </Label>
            <Input
              className="h-8 text-sm"
              disabled
              title={value}
              value={value}
            />
          </div>
        )
      })}
    </div>
  </div>
)

type OrganizationHubspotDataProps = {
  orgId?: string
}

const OrganizationHubspotData = ({ orgId }: OrganizationHubspotDataProps) => {
  const { data, isLoading } = useQueryGetOrganizationHubspotData(orgId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icons.spinner className="w-6 h-6" />
        <span className="ml-2 text-sm text-muted-foreground">
          Loading HubSpot data...
        </span>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-sm text-muted-foreground">
          No HubSpot data available for this organization. Make sure a HubSpot
          company is linked in the Settings tab.
        </span>
      </div>
    )
  }

  // Collect remaining properties not in any field group
  const remainingProperties = Object.entries(data.properties || {})
    .filter(
      ([key, val]) =>
        !KNOWN_KEYS.has(key) && val !== null && val !== undefined && val !== ''
    )
    .sort(([a], [b]) => a.localeCompare(b))

  return (
    <div className="space-y-6">
      {/* Header with company name and link */}
      <div className="flex items-center justify-between pt-3">
        <div>
          <h2 className="text-lg font-semibold text-white">
            {data.company_name || 'Unknown Company'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {data.domain && <span>{data.domain}</span>}
            {data.domain && data.company_id && <span> &middot; </span>}
            {data.company_id && (
              <span>HubSpot ID: {data.company_id}</span>
            )}
          </p>
        </div>
        {data.company_id && (
          <a
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            href={getHubspotCompanyUrl(data.company_id)}
            rel="noopener noreferrer"
            target="_blank"
          >
            Open in HubSpot
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      <div className="flex space-x-8">
        {/* Left column */}
        <div className="w-1/2 space-y-6">
          <FieldGroup data={data} fields={BASIC_FIELDS} title="Basic" />
          <FieldGroup
            data={data}
            fields={ADOPTION_FIELDS}
            title="Adoption"
          />
        </div>

        {/* Right column */}
        <div className="w-1/2 space-y-6">
          <FieldGroup
            data={data}
            fields={SIZE_MRR_FIELDS}
            title="Size & MRR"
          />
          <FieldGroup
            data={data}
            fields={IT_LANDSCAPE_FIELDS}
            title="IT Landscape"
          />
        </div>
      </div>

      {/* All other properties */}
      {remainingProperties.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-primary border-b pb-1">
            All Other Properties
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {remainingProperties.map(([key, val]) => (
              <div className="space-y-1" key={key}>
                <Label className="text-xs text-muted-foreground">
                  {formatPropertyLabel(key)}
                </Label>
                <Input
                  className="h-8 text-sm"
                  disabled
                  title={typeof val === 'string' ? val : JSON.stringify(val)}
                  value={typeof val === 'string' ? val : JSON.stringify(val)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default OrganizationHubspotData
